import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFirestore, collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import app from "./firebase";
import styles from "./ProductionBatchDetailsDashboard.module.css";

const db = getFirestore(app);

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
  const d = toJsDate(value);
  return d ? d.toLocaleString() : "-";
};

const compareValues = (a, b) => {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  const da = toJsDate(a);
  const dbb = toJsDate(b);
  if (da && dbb) return da.getTime() - dbb.getTime();

  const na = typeof a === "number" ? a : Number(a);
  const nb = typeof b === "number" ? b : Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;

  return String(a).localeCompare(String(b));
};

export default function ProductionBatchDetailsDashboard() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("ingredientName");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [eventSnap, usageSnap] = await Promise.all([
          getDoc(doc(db, "productionEvents", eventId)),
          getDocs(query(collection(db, "ingredient_usage_history"), where("productionEventId", "==", eventId)))
        ]);

        setEvent(eventSnap.exists() ? { id: eventSnap.id, ...eventSnap.data() } : null);
        setRows(usageSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error fetching batch details:", err);
        setEvent(null);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [eventId]);

  const filteredAndSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = rows.filter(r => {
      if (!term) return true;
      const hay = [r.ingredientName, r.batchCode, r.productName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });

    const dir = sortDir === "asc" ? 1 : -1;
    return filtered.sort((a, b) => dir * compareValues(a?.[sortField], b?.[sortField]));
  }, [rows, search, sortField, sortDir]);

  const fallbackRows = useMemo(() => {
    if (!event || rows.length > 0) return [];
    const ingredients = Array.isArray(event.ingredients) ? event.ingredients : [];
    return ingredients.map((ing, idx) => ({
      id: `fallback:${event.id}:${idx}`,
      ingredientName: ing.name || "-",
      unit: ing.stockUnit || ing.unit || "",
      quantityUsed: ing.quantityStockUnit ?? ing.quantity ?? null,
      lotPurchaseDate: null,
      lotRemainingAfter: null
    }));
  }, [event, rows.length]);

  if (loading) return <p className={styles.loading}>Loading batch...</p>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>{event?.batchCode || "Batch Details"}</h2>
          <div className={styles.subtitle}>
            {event?.productName ? `${event.productName} • ` : ""}{formatDateTime(event?.date)}
          </div>
        </div>
        <button className={styles.backButton} onClick={() => navigate(event?.productId ? `/production-history/${event.productId}` : "/production-history")}>
          Back
        </button>
      </div>

      <div className={styles.controls}>
        <input
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ingredient..."
        />
        <select className={styles.select} value={sortField} onChange={(e) => setSortField(e.target.value)}>
          <option value="ingredientName">Ingredient</option>
          <option value="quantityUsed">Quantity Used</option>
          <option value="lotPurchaseDate">Purchase Date</option>
        </select>
        <select className={styles.select} value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
          <option value="asc">Asc</option>
          <option value="desc">Desc</option>
        </select>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Ingredient</th>
              <th>Quantity Used</th>
              <th>Purchase Date (FIFO lot)</th>
              <th>Lot</th>
              <th>Lot Remaining</th>
            </tr>
          </thead>
          <tbody>
            {(filteredAndSorted.length > 0 ? filteredAndSorted : fallbackRows).map(r => (
              <tr key={r.id}>
                <td>{r.ingredientName || "-"}</td>
                <td>{r.quantityUsed === null || r.quantityUsed === undefined ? "-" : `-${Math.abs(Number(r.quantityUsed))}`} {r.unit || ""}</td>
                <td>{formatDateTime(r.lotPurchaseDate)}</td>
                <td>{r.lotId ? String(r.lotId).slice(-6) : "-"}</td>
                <td>{r.lotRemainingAfter ?? "-"} {r.unit || ""}</td>
              </tr>
            ))}
            {filteredAndSorted.length === 0 && fallbackRows.length === 0 && (
              <tr><td className={styles.empty} colSpan={5}>No ingredients found for this batch</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
